import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import type { InvitationData } from '@/src/modules/templates/types';
import type { ApiTemplate, ApiInvitation } from '@/src/types';

export interface DraftPreviewData {
  templateId: string;
  occasionType: string;
  eventName?: string | null;
  eventDate?: string | null;
  eventVenue?: string | null;
  hostName?: string | null;
  customMessage?: string | null;
  rsvpLink?: string | null;
  mediaUrls?: string[];
  customData?: Record<string, unknown> | null;
  /** Optional: full ApiTemplate shape needed to render JSON-schema templates */
  template?: ApiTemplate | null;
}

interface Props {
  open: boolean;
  data: DraftPreviewData;
  onClose: () => void;
  /** Optional label for the CTA button (e.g. "Publish Now"). Omit to hide the button. */
  ctaLabel?: string;
  onCta?: () => void;
}

/**
 * Full-screen modal that renders any InviteCloud template with real draft data.
 * Works for code-rendered templates (registry), JSON-schema templates, and shows
 * a clear placeholder for anything else.
 *
 * Usage:
 *   <DraftPreviewModal open={show} data={draftData} onClose={() => setShow(false)} />
 */
export function DraftPreviewModal({ open, data, onClose, ctaLabel, onCta }: Props) {
  const invitationData: InvitationData = {
    templateId: data.templateId,
    occasionType: data.occasionType as InvitationData['occasionType'],
    eventName: data.eventName || 'Your Event Name',
    eventDate:
      data.eventDate ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    eventVenue: data.eventVenue || 'The Grand Venue',
    hostName: data.hostName || 'Your Name',
    customMessage: data.customMessage ?? undefined,
    rsvpLink: data.rsvpLink ?? undefined,
    mediaUrls: data.mediaUrls ?? [],
    customData: data.customData ?? undefined,
  };

  // ApiInvitation shape required by JsonTemplateRenderer
  const apiInvitationData: ApiInvitation = {
    id: `draft-preview-${data.templateId}`,
    publicSlug: null,
    templateId: data.templateId,
    occasionType: data.occasionType as ApiInvitation['occasionType'],
    status: 'DRAFT',
    hostedUrl: null,
    eventName: invitationData.eventName,
    eventDate: invitationData.eventDate,
    eventVenue: invitationData.eventVenue,
    hostName: invitationData.hostName,
    customMessage: invitationData.customMessage,
    rsvpLink: invitationData.rsvpLink,
    mediaUrls: invitationData.mediaUrls,
    customData: invitationData.customData ?? undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-[#1A202C] overflow-y-auto"
        >
          {/* Floating control bar */}
          <div className="fixed top-4 left-4 z-[210] flex gap-3">
            <Button
              variant="secondary"
              className="bg-black/60 hover:bg-black/80 text-white border-none rounded-full px-5 py-2.5 backdrop-blur-md shadow-lg flex items-center gap-2"
              onClick={onClose}
            >
              <Icons.ArrowLeft size={16} /> Close Preview
            </Button>
            {ctaLabel && onCta && (
              <Button
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#1A202C] rounded-full px-6 py-2.5 font-bold shadow-lg"
                onClick={onCta}
              >
                {ctaLabel}
              </Button>
            )}
          </div>

          {/* Draft badge */}
          <div className="fixed top-4 right-4 z-[210] bg-black/50 backdrop-blur-md text-white text-xs font-semibold uppercase tracking-widest rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
            <Icons.Eye size={13} />
            Draft Preview
          </div>

          {/* Template render area */}
          <div className="w-full min-h-screen" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}>
              {registry.has(data.templateId) ? (
                <TemplateRenderer templateId={data.templateId} data={invitationData} />
              ) : data.template?.renderMode === 'json-renderer' &&
                data.template?.layoutSchema ? (
                <JsonTemplateRenderer
                  schema={data.template.layoutSchema}
                  data={apiInvitationData}
                  theme={data.template.themeConfig ?? undefined}
                  mode="preview"
                />
              ) : (
                <SceneExperienceRenderer manifest={resolveExperienceManifest(data.templateId, apiInvitationData)} />
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
